/**
 * Secure Institutional Disclosure — browser-side cryptography.
 *
 * PHASE-1 CHECKPOINT #4 INVARIANT (do not violate):
 *   VeraLok MUST NOT see the plaintext disclosure payload at any point.
 *   Concretely, this module:
 *     • generates the institution's X25519 keypair entirely in-browser;
 *     • wraps the private key with a passphrase using Argon2id + NaCl
 *       secretbox in-browser and uploads only the wrapped blob;
 *     • decrypts institution-inbound ciphertext (sealed_box) in-browser
 *       and never sends the plaintext back to VeraLok;
 *     • never logs / persists / transmits the passphrase, plaintext
 *       private key or decrypted payload.
 *
 * Depends on `libsodium-wrappers` (WebAssembly build of NaCl / libsodium).
 */
import sodium from "libsodium-wrappers-sumo";

let _readyPromise = null;

/** Await libsodium's WASM init. Safe to call repeatedly. */
export async function ready() {
  if (!_readyPromise) {
    _readyPromise = sodium.ready.then(() => sodium);
  }
  return _readyPromise;
}

// ────────────────────────────────────────────────────────────────────────────
// Keypair generation + passphrase wrapping
// ────────────────────────────────────────────────────────────────────────────

/**
 * Generate a fresh X25519 keypair and return both the raw public/private
 * bytes (base64) and a passphrase-wrapped private-key blob suitable for
 * escrow to VeraLok.
 *
 * The wrapped blob uses:
 *   • KDF: Argon2id (interactive opslimit / interactive memlimit)
 *   • cipher: crypto_secretbox_easy (XSalsa20-Poly1305)
 *   • format: nonce || ciphertext
 */
export async function generateInstitutionKeypair({ passphrase, keyId }) {
  const s = await ready();
  if (!passphrase || passphrase.length < 12) {
    throw new Error("passphrase_too_short");
  }
  if (!keyId || keyId.length < 8) {
    throw new Error("key_id_required");
  }

  const kp = s.crypto_box_keypair(); // X25519 (Curve25519) keypair

  // Argon2id KDF params — interactive is appropriate for admin-issued
  // passphrases (memory-hard but fast enough for browser). Params are
  // uploaded so unwrap-on-new-device is deterministic.
  const salt = s.randombytes_buf(s.crypto_pwhash_SALTBYTES);
  const opslimit = s.crypto_pwhash_OPSLIMIT_INTERACTIVE;
  const memlimit = s.crypto_pwhash_MEMLIMIT_INTERACTIVE;
  const wrapKey = s.crypto_pwhash(
    s.crypto_secretbox_KEYBYTES,
    passphrase,
    salt,
    opslimit,
    memlimit,
    s.crypto_pwhash_ALG_ARGON2ID13,
  );

  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const ct = s.crypto_secretbox_easy(kp.privateKey, nonce, wrapKey);
  // wrapped blob = nonce || ciphertext
  const wrapped = new Uint8Array(nonce.length + ct.length);
  wrapped.set(nonce, 0);
  wrapped.set(ct, nonce.length);

  return {
    key_id: keyId,
    algorithm: "curve25519",
    public_key_b64: s.to_base64(kp.publicKey, s.base64_variants.ORIGINAL),
    // For internal use only — the caller MUST discard this after upload;
    // it is never sent to VeraLok.
    private_key_b64_do_not_upload: s.to_base64(
      kp.privateKey,
      s.base64_variants.ORIGINAL,
    ),
    wrapped_private_key_b64: s.to_base64(wrapped, s.base64_variants.ORIGINAL),
    kdf: "argon2id",
    kdf_params: {
      salt_b64: s.to_base64(salt, s.base64_variants.ORIGINAL),
      opslimit,
      memlimit,
      alg: "argon2id13",
    },
  };
}

/**
 * Given the escrowed blob + passphrase, re-derive the private key
 * IN-BROWSER. Used when an institution admin restores access on a new
 * device. VeraLok never sees the passphrase or the unwrapped private
 * key.
 */
export async function unwrapPrivateKey({
  wrapped_private_key_b64,
  kdf_params,
  passphrase,
}) {
  const s = await ready();
  const wrapped = s.from_base64(
    wrapped_private_key_b64,
    s.base64_variants.ORIGINAL,
  );
  const nonceLen = s.crypto_secretbox_NONCEBYTES;
  const nonce = wrapped.slice(0, nonceLen);
  const ct = wrapped.slice(nonceLen);
  const salt = s.from_base64(kdf_params.salt_b64, s.base64_variants.ORIGINAL);
  const wrapKey = s.crypto_pwhash(
    s.crypto_secretbox_KEYBYTES,
    passphrase,
    salt,
    kdf_params.opslimit,
    kdf_params.memlimit,
    s.crypto_pwhash_ALG_ARGON2ID13,
  );
  let privateKey;
  try {
    privateKey = s.crypto_secretbox_open_easy(ct, nonce, wrapKey);
  } catch (_e) {
    throw new Error("passphrase_incorrect");
  }
  return {
    private_key_b64: s.to_base64(privateKey, s.base64_variants.ORIGINAL),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Ciphertext decryption (institution side)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Decrypt a sealed-box ciphertext produced by a citizen device against
 * the institution's public key. Requires the private key raw bytes
 * previously unwrapped in-browser via `unwrapPrivateKey`.
 *
 * Returns a UTF-8 string if the plaintext is valid UTF-8, otherwise
 * returns the raw Uint8Array so the caller can render as file.
 */
export async function decryptSealedBox({
  ciphertext_b64,
  public_key_b64,
  private_key_b64,
}) {
  const s = await ready();
  const ct = s.from_base64(ciphertext_b64, s.base64_variants.ORIGINAL);
  const pk = s.from_base64(public_key_b64, s.base64_variants.ORIGINAL);
  const sk = s.from_base64(private_key_b64, s.base64_variants.ORIGINAL);
  let plaintext;
  try {
    plaintext = s.crypto_box_seal_open(ct, pk, sk);
  } catch (_e) {
    throw new Error("decryption_failed");
  }
  try {
    const asText = new TextDecoder("utf-8", { fatal: true }).decode(plaintext);
    return { as_text: asText, bytes: plaintext };
  } catch (_e) {
    return { as_text: null, bytes: plaintext };
  }
}

/**
 * Sealed-box encrypt in-browser (used only in tests / demo flows — the
 * citizen mobile app is the real producer). Kept here so the
 * "institution demo → citizen synthetic upload" round-trip is testable
 * from the Institution Portal without the mobile app.
 */
export async function encryptSealedBox({ plaintext_utf8, public_key_b64 }) {
  const s = await ready();
  const pk = s.from_base64(public_key_b64, s.base64_variants.ORIGINAL);
  const pt = new TextEncoder().encode(plaintext_utf8);
  const ct = s.crypto_box_seal(pt, pk);
  return {
    ciphertext_b64: s.to_base64(ct, s.base64_variants.ORIGINAL),
    algorithm: "nacl_sealed_box",
  };
}
